import React, { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  color: string;
  type: 'droplet' | 'sheet' | 'sparkle';
  angle: number;
  rotationSpeed: number;
  opacity: number;
  driftFrequency: number;
  driftPhase: number;
}

const CMYK_COLORS = [
  'rgba(0, 188, 212, ',  // Cyan
  'rgba(233, 30, 140, ', // Magenta
  'rgba(255, 193, 7, ',  // Yellow
  'rgba(59, 91, 219, ',  // Royal Blue / Key
];

const AntigravityAnimation: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mouseRef = useRef<{ x: number; y: number; active: boolean }>({ x: 0, y: 0, active: false });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let particles: Particle[] = [];
    const maxParticles = 40;

    // Helper to generate a particle
    const createParticle = (x?: number, y?: number, isBurst = false): Particle => {
      const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
      const colorBase = CMYK_COLORS[Math.floor(Math.random() * CMYK_COLORS.length)];
      // Particles are slightly more vibrant/opaque in dark mode
      const opacity = isBurst ? 0.8 : (Math.random() * 0.3 + 0.15);
      
      const types: Particle['type'][] = ['droplet', 'sheet', 'sparkle'];
      const type = types[Math.floor(Math.random() * types.length)];

      const radius = type === 'droplet' 
        ? Math.random() * 8 + 4
        : type === 'sheet'
          ? Math.random() * 12 + 6
          : Math.random() * 6 + 3;

      // Anti-gravity velocities: slow upward motion (-vy)
      const baseVy = isBurst ? (Math.random() * 4 - 2) : -(Math.random() * 0.8 + 0.3);
      const baseVx = isBurst ? (Math.random() * 4 - 2) : (Math.random() * 0.4 - 0.2);

      return {
        x: x ?? Math.random() * canvas.width,
        y: y ?? (x ? Math.random() * canvas.height : canvas.height + 20),
        vx: baseVx,
        vy: baseVy,
        radius,
        color: `${colorBase}${opacity})`,
        type,
        angle: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() * 0.02 - 0.01),
        opacity: opacity,
        driftFrequency: Math.random() * 0.005 + 0.002,
        driftPhase: Math.random() * Math.PI * 2,
      };
    };

    // Resize canvas
    const handleResize = () => {
      const parent = canvas.parentElement;
      if (parent) {
        canvas.width = parent.clientWidth;
        canvas.height = parent.clientHeight;
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    // Initialize particles
    for (let i = 0; i < maxParticles; i++) {
      // Distribute throughout the initial viewport height
      particles.push(createParticle(undefined, Math.random() * canvas.height));
    }

    // Draw functions
    const drawDroplet = (p: Particle) => {
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
      ctx.fillStyle = p.color;
      ctx.fill();
    };

    const drawSheet = (p: Particle) => {
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.angle);
      ctx.fillStyle = p.color;
      // Draw rectangular sheet of paper
      ctx.fillRect(-p.radius, -p.radius * 0.7, p.radius * 2, p.radius * 1.4);
      // Give a subtle white/light border to sheets of paper to pop
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
      ctx.lineWidth = 1;
      ctx.strokeRect(-p.radius, -p.radius * 0.7, p.radius * 2, p.radius * 1.4);
      ctx.restore();
    };

    const drawSparkle = (p: Particle) => {
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.angle);
      ctx.fillStyle = p.color;
      ctx.beginPath();
      for (let i = 0; i < 4; i++) {
        ctx.lineTo(0, -p.radius);
        ctx.lineTo(p.radius * 0.25, -p.radius * 0.25);
        ctx.rotate(Math.PI / 2);
      }
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    };

    // Render loop
    const tick = (time: number) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw and update
      particles.forEach((p, idx) => {
        // Move upward with sine wave horizontal drift
        p.y += p.vy;
        p.x += p.vx + Math.sin(time * p.driftFrequency + p.driftPhase) * 0.25;
        p.angle += p.rotationSpeed;

        // Interaction with mouse repulsion
        if (mouseRef.current.active) {
          const dx = p.x - mouseRef.current.x;
          const dy = p.y - mouseRef.current.y;
          const distance = Math.hypot(dx, dy);
          const repulsionRadius = 140;

          if (distance < repulsionRadius) {
            const force = (repulsionRadius - distance) / repulsionRadius;
            const angle = Math.atan2(dy, dx);
            
            // Push away
            p.x += Math.cos(angle) * force * 5;
            p.y += Math.sin(angle) * force * 5;
            
            // Increase rotation slightly
            p.angle += p.rotationSpeed * 3;
          }
        }

        // Draw based on type
        if (p.type === 'droplet') drawDroplet(p);
        else if (p.type === 'sheet') drawSheet(p);
        else if (p.type === 'sparkle') drawSparkle(p);

        // Respawn if off screen
        if (p.y < -30 || p.x < -30 || p.x > canvas.width + 30) {
          // Replace it with a new one at the bottom
          particles[idx] = createParticle();
        }
      });

      animationFrameId = requestAnimationFrame(tick);
    };

    animationFrameId = requestAnimationFrame(tick);

    // Interaction Listeners (Bound to the parent for safety)
    const parent = canvas.parentElement;
    
    const handleMouseMove = (e: MouseEvent) => {
      if (!parent) return;
      const rect = parent.getBoundingClientRect();
      mouseRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
        active: true,
      };
    };

    const handleMouseLeave = () => {
      mouseRef.current.active = false;
    };

    const handleClick = (e: MouseEvent) => {
      if (!parent) return;
      const rect = parent.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;

      // Burst! Spawn 15 colorful particles
      for (let i = 0; i < 15; i++) {
        particles.push(createParticle(clickX, clickY, true));
      }

      // Cap particles length to prevent leaks
      if (particles.length > 100) {
        particles = particles.slice(-80);
      }
    };

    if (parent) {
      parent.addEventListener('mousemove', handleMouseMove);
      parent.addEventListener('mouseleave', handleMouseLeave);
      parent.addEventListener('click', handleClick);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameId);
      if (parent) {
        parent.removeEventListener('mousemove', handleMouseMove);
        parent.removeEventListener('mouseleave', handleMouseLeave);
        parent.removeEventListener('click', handleClick);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none', // Critical: Let events pass through to buttons
        zIndex: 0,
      }}
    />
  );
};

export default AntigravityAnimation;
